<template>
  <div>
    <head>
      <title>Schedule Procedure | County Hospital</title>
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar/>
    <div id="content" class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <!-- Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Schedule Procedure</h4>
                <button class="btn btn-sm btn-outline-primary" @click="$router.push('scheduled-procedures')">
                  <i class="bx bx-list-ul me-1"></i> View Scheduled
                </button>
              </div>
            </div>
          </div>

          <!-- Form -->
          <div class="row justify-content-center">
            <div class="col-lg-9 col-xl-8">
              <div class="card border shadow-sm">
                <div class="card-body p-4">

                  <div v-if="message" class="alert" :class="success ? 'alert-success' : 'alert-danger'">
                    <i class="bx me-1" :class="success ? 'bx-check-circle' : 'bx-error-circle'"></i>
                    {{ message }}
                  </div>

                  <h5 class="mb-3">Patient & Visit</h5>
                  <div class="row g-3 mb-4">
                    <div class="col-md-6">
                      <label class="form-label">Search Patient</label>
                      <div class="input-group">
                        <input type="text" class="form-control"
                               v-model.trim="patientQuery"
                               placeholder="Patient No, ID, name, phone..."
                               @keyup.enter="searchPatient">
                        <button class="btn btn-primary" type="button" @click="searchPatient" :disabled="searching">
                          <span v-if="searching" class="spinner-border spinner-border-sm"></span>
                          <span v-else>Search</span>
                        </button>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Visit ID (optional)</label>
                      <input type="text" class="form-control" v-model.trim="form.visitID" placeholder="e.g. V0429001">
                    </div>
                  </div>

                  <div v-if="patient" class="alert alert-light border d-flex align-items-start mb-4">
                    <i class="bx bx-user me-2 fs-4 text-primary"></i>
                    <div class="flex-grow-1">
                      <div><strong>{{ patient.firstName }} {{ patient.lastName }}</strong>
                        <span class="text-muted ms-2">#{{ patient.patientNo }}</span>
                      </div>
                      <small class="text-muted">
                        {{ patient.gender }} · {{ patient.dateOfBirth }} · {{ patient.phoneNumber }}
                      </small>
                    </div>
                    <button class="btn btn-sm btn-outline-secondary" @click="patient = null">Change</button>
                  </div>

                  <h5 class="mb-3">Procedure Details</h5>
                  <div class="row g-3 mb-4">
                    <div class="col-md-6">
                      <label class="form-label">Procedure <span class="text-danger">*</span></label>
                      <select v-model="form.procedureTypeID" class="form-select" @change="onProcedureSelected">
                        <option value="">-- Select --</option>
                        <option v-for="p in procedureTypes" :key="p.procedureTypeID" :value="p.procedureTypeID">
                          {{ p.procedureName }}
                          <span v-if="p.category"> ({{ p.category }})</span>
                        </option>
                      </select>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Date <span class="text-danger">*</span></label>
                      <input type="date" class="form-control" v-model="form.scheduledDate" :min="todayIso">
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Time <span class="text-danger">*</span></label>
                      <input type="time" class="form-control" v-model="form.scheduledTime">
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Performing Doctor</label>
                      <select v-model="selectedDoctorJSON" class="form-select" @change="onDoctorSelected">
                        <option value="">-- Select --</option>
                        <option v-for="d in doctors" :key="d.staffID" :value="JSON.stringify(d)">
                          {{ d.firstName }} {{ d.lastName }} <span v-if="d.specialization"> · {{ d.specialization }}</span>
                        </option>
                      </select>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Priority</label>
                      <select v-model="form.priority" class="form-select">
                        <option value="ROUTINE">Routine</option>
                        <option value="URGENT">Urgent</option>
                        <option value="EMERGENCY">Emergency</option>
                      </select>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Estimated Cost (KES)</label>
                      <input type="text" class="form-control" :value="estimatedFee" disabled>
                    </div>
                    <div class="col-12">
                      <label class="form-label">Pre-op Diagnosis <span class="text-danger">*</span></label>
                      <textarea v-model="form.preOpDiagnosis" class="form-control" rows="2"
                                placeholder="Working diagnosis, indication for procedure"></textarea>
                    </div>
                    <div class="col-12">
                      <label class="form-label">Notes</label>
                      <textarea v-model="form.notes" class="form-control" rows="2"
                                placeholder="Any special instructions, fasting requirements, etc."></textarea>
                    </div>
                  </div>

                  <div class="d-flex justify-content-end">
                    <button class="btn btn-light me-2" @click="resetForm">Reset</button>
                    <button class="btn btn-primary" :disabled="!canSubmit || submitting" @click="submit">
                      <span v-if="submitting"><span class="spinner-border spinner-border-sm me-1"></span>Saving...</span>
                      <span v-else><i class="bx bx-calendar-plus me-1"></i> Schedule Procedure</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          </div>
        </div>
      </div>
    </div>
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import { execute, hospital, getRight } from "@/api";

export default {
  name: "ScheduleProcedure",
  components: { NavigationBar },
  data() {
    return {
      message: null,
      success: false,
      searching: false,
      submitting: false,
      patientQuery: '',
      patient: null,
      procedureTypes: [],
      doctors: [],
      selectedDoctorJSON: '',
      form: {
        visitID: '',
        procedureTypeID: '',
        procedureName: '',
        scheduledDate: '',
        scheduledTime: '',
        performingDoctorID: '',
        performingDoctorName: '',
        preOpDiagnosis: '',
        priority: 'ROUTINE',
        notes: ''
      },
      estimatedFee: '0.00'
    };
  },
  computed: {
    todayIso() { return new Date().toISOString().slice(0, 10); },
    canSubmit() {
      return this.patient
          && this.form.procedureTypeID
          && this.form.scheduledDate
          && this.form.scheduledTime
          && this.form.preOpDiagnosis;
    }
  },
  mounted() {
    this.loadProcedureTypes();
    this.loadDoctors();
  },
  methods: {
    getRight(t) { return getRight(t); },

    loadProcedureTypes() {
      const data = new FormData();
      data.append('function', 'getProcedureTypes');
      execute(data, hospital).then(res => {
        if (res.data && res.data.success) this.procedureTypes = res.data.data.procedureTypes || [];
      }).catch(() => {});
    },
    loadDoctors() {
      const data = new FormData();
      data.append('function', 'getDoctors');
      execute(data, hospital).then(res => {
        if (res.data && res.data.success) this.doctors = res.data.data.doctors || res.data.data.staff || [];
      }).catch(() => {});
    },
    searchPatient() {
      if (!this.patientQuery) return;
      this.searching = true;
      this.message = null;
      const data = new FormData();
      data.append('function', 'searchPatient');
      data.append('search', this.patientQuery);
      execute(data, hospital).then(res => {
        this.searching = false;
        if (res.data && res.data.success) {
          const list = res.data.data.patients || [];
          this.patient = list[0] || null;
          if (!this.patient) {
            this.success = false;
            this.message = `No patient found for "${this.patientQuery}"`;
          }
        } else {
          this.success = false;
          this.message = (res.data && res.data.message) || 'Search failed';
        }
      }).catch(e => {
        this.searching = false;
        this.success = false;
        this.message = e.message || 'Network error';
      });
    },
    onProcedureSelected() {
      const sel = this.procedureTypes.find(p => p.procedureTypeID === this.form.procedureTypeID);
      if (sel) {
        this.form.procedureName = sel.procedureName;
        this.estimatedFee = sel.standardFee
            ? Number(sel.standardFee).toLocaleString('en-KE', { minimumFractionDigits: 2 })
            : '—';
      }
    },
    onDoctorSelected() {
      try {
        const d = JSON.parse(this.selectedDoctorJSON || '{}');
        this.form.performingDoctorID = d.staffID || d.idNo || '';
        this.form.performingDoctorName = ((d.firstName || '') + ' ' + (d.lastName || '')).trim();
      } catch { /* ignore */ }
    },
    submit() {
      if (!this.canSubmit) return;
      this.submitting = true;
      this.message = null;

      const facilityID = sessionStorage.getItem('facilityID') || '';
      const facilityName = sessionStorage.getItem('facilityName') || '';

      const data = new FormData();
      data.append('function', 'scheduleProcedure');
      data.append('facilityID', facilityID);
      data.append('facilityName', facilityName);
      data.append('visitID', this.form.visitID);
      data.append('visitNo', '');
      data.append('patientID', this.patient.patientID || '');
      data.append('patientNo', this.patient.patientNo || '');
      data.append('patientName', `${this.patient.firstName || ''} ${this.patient.lastName || ''}`.trim());
      data.append('procedureTypeID', this.form.procedureTypeID);
      data.append('procedureName', this.form.procedureName);
      data.append('scheduledDate', this.form.scheduledDate);
      data.append('scheduledTime', this.form.scheduledTime);
      data.append('performingDoctorID', this.form.performingDoctorID);
      data.append('performingDoctorName', this.form.performingDoctorName);
      data.append('preOpDiagnosis', this.form.preOpDiagnosis);
      data.append('priority', this.form.priority);
      data.append('notes', this.form.notes);
      data.append('scheduledBy', sessionStorage.getItem('sessionNames') || '');
      data.append('sessionNames', sessionStorage.getItem('sessionNames') || '');
      data.append('sessionIdNo', sessionStorage.getItem('sessionIdNo') || '');

      execute(data, hospital).then(res => {
        this.submitting = false;
        if (res.data && res.data.success) {
          this.success = true;
          this.message = `Procedure scheduled. Reference: ${res.data.data.procedureNo}`;
          setTimeout(() => this.$router.push('scheduled-procedures'), 1200);
        } else {
          this.success = false;
          this.message = (res.data && res.data.message) || 'Could not schedule procedure';
        }
      }).catch(e => {
        this.submitting = false;
        this.success = false;
        this.message = e.message || 'Network error';
      });
    },
    resetForm() {
      this.patient = null;
      this.patientQuery = '';
      this.selectedDoctorJSON = '';
      this.estimatedFee = '0.00';
      this.message = null;
      this.form = {
        visitID: '', procedureTypeID: '', procedureName: '',
        scheduledDate: '', scheduledTime: '',
        performingDoctorID: '', performingDoctorName: '',
        preOpDiagnosis: '', priority: 'ROUTINE', notes: ''
      };
    }
  }
};
</script>
